import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  OnChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';

import { BrowserService } from '../services/browser.service';
import { DeviceService } from '../services/device/device.service';
import { VideoEffectsProcessor } from '../services/device/video-effects-processor';
import { VideoEffect } from '../state/app';

export const CAMERA_PLAY_TIMEOUT_MS = 10000;

@Component({
  selector: 'app-camera',
  styleUrls: ['./camera.component.scss'],
  templateUrl: './camera.component.html',
  standalone: false,
})
export class CameraComponent extends Loggable implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('previewVideo') public previewVideo?: ElementRef<HTMLVideoElement>;
  @Input() public previewEffects?: VideoEffect;
  @Input() public onVideoPlaying?: () => void;
  @Input() public onVideoTimeout?: () => void;
  @Input() public hasBorder = false;

  private videoEffectsProcessor: VideoEffectsProcessor;

  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceService,
    private browserService: BrowserService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('CameraComponent');

    this.videoEffectsProcessor = new VideoEffectsProcessor(
      this.browserService.browserType,
      this.store,
      this.previewEffects,
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    for (const propName in changes) {
      if (propName === 'previewEffects') {
        this.videoEffectsProcessor.setEffects(changes[propName].currentValue).catch((e) => {
          this.error('Failed to set preview effects', e);
        });
      }
    }
  }

  public ngAfterViewInit(): void {
    let { cameraStream$ } = this.deviceService;
    if (this.previewEffects) {
      cameraStream$ = this.deviceService.rawCameraStream$;
    }
    cameraStream$.pipe(takeUntil(this.destroyed$)).subscribe((stream) => {
      this.info('Got camera stream', { stream });
      this.updateCameraStream(stream).catch((e) => {
        this.error('Failed to update camera stream', e);
      });
    });
  }

  public ngOnDestroy(): void {
    this.cdRef.detach();
    const { nativeElement } = this.previewVideo ?? {};
    try {
      nativeElement?.pause();
      if (nativeElement) {
        nativeElement.srcObject = null;
        nativeElement.onplaying = null;
        nativeElement.onerror = null;
      }
    } catch (e) {
      this.info('Failed to pause camera stream on destruction', e);
    }
    if (nativeElement && nativeElement.srcObject instanceof MediaStream) {
      this.stopStream(nativeElement.srcObject as MediaStream);
    }
    this.videoEffectsProcessor.destroy();

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private stopStream(stream: MediaStream | undefined): void {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }

  private async updateCameraStream(_stream: MediaStream | undefined): Promise<void> {
    let stream = _stream;

    if (stream === DUMMY_VIDEO_STREAM) {
      return;
    }

    if (stream && this.previewEffects) {
      stream = this.videoEffectsProcessor.processVideoStream(stream);
    }

    // let timeout: Timeout;

    this.info('Received new camera stream', stream?.id);
    if (!this.previewVideo) {
      this.warn('No preview video element to update');
      return;
    }

    const { nativeElement } = this.previewVideo;
    // if (stream) {
    //   timeout = setTimeout(() => {
    //     this.error('Camera timeout when trying to play stream', { id: stream?.id });
    //     this.onVideoTimeout?.();
    //   }, CAMERA_PLAY_TIMEOUT_MS);
    // }

    nativeElement.srcObject = stream ?? null;
    nativeElement.onplaying = (): void => {
      if (!stream) {
        this.warn('Got onplaying event for camera stream when no stream was set');
        return;
      }
      this.info('Camera stream is playing', { id: stream.id });
      // if (timeout) {
      //   clearTimeout(timeout);
      // }
      this.onVideoPlaying?.();
    };
    // added this to handle the case where the camera stream is not playing
    // commenting out existing timeout logic as it is firing on resolution change
    nativeElement.onerror = (e): void => {
      this.error('Camera timeout when trying to play stream', { id: stream?.id }, e);
      this.onVideoTimeout?.();
    };

    if (!stream) {
      nativeElement.pause();
      return;
    }

    await nativeElement.play().catch((e) => {
      this.error(`Failed to play camera stream ${stream?.id}`, e);
    });
  }
}
